import { environmentLabel, isEnvironmentFaulty } from 'lib';
import { useTranslation } from 'react-i18next';

/**
 * States which environment this view covers, and why its numbers are only part of the picture.
 *
 * Not decoration. Each environment has its own database, so this screen can only ever show what
 * this environment did — and every environment authenticates to the match data provider with the
 * same key. An admin reading "remaining allowance" here as authoritative would believe there is
 * budget left that another environment has already spent, which is exactly the misreading the
 * activity log was built to prevent.
 */
const EnvironmentNotice = ({ environment, environmentRaw, allowanceShared }) => {
  const { t } = useTranslation();
  const faulty = isEnvironmentFaulty(environment);

  return (
    <div
      className={`rounded-xl border p-3 mb-4 ${
        faulty ? 'bg-[#FFF1F2] border-[#FECDD3]' : 'bg-[#EFF6FF] border-[#BFDBFE]'
      }`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-[#334155] uppercase tracking-wide">
          {t('admin.activityLog.notice.scope', { defaultValue: 'Showing' })}
        </span>
        <span className="px-2 py-[2px] rounded-md text-[11px] font-bold bg-white border border-[#BFDBFE] text-[#2563EB]">
          {environmentLabel(environment)}
        </span>
        <span className="text-[12px] text-[#475569]">
          {t('admin.activityLog.notice.onlyThis', {
            defaultValue:
              'This environment only. Each environment has its own database, so other environments are not visible here — combine CSV exports to see the whole platform.'
          })}
        </span>
      </div>

      {environment === 'unrecognised' && (
        <div className="mt-2 text-[12px] text-[#E11D48]">
          {t('admin.activityLog.notice.unrecognised', {
            defaultValue:
              'APP_ENV is set to a value that is not one of development, staging, uat or production, so activity cannot be attributed to a known environment. Fix the configuration.'
          })}
          {environmentRaw ? (
            <span className="font-mono"> (APP_ENV=&quot;{environmentRaw}&quot;)</span>
          ) : null}
        </div>
      )}

      {environment === 'unknown' && (
        <div className="mt-2 text-[12px] text-[#E11D48]">
          {t('admin.activityLog.notice.unknown', {
            defaultValue:
              'APP_ENV is not set, so this deployment cannot say which environment it is. Activity is recorded as "not configured" rather than assumed to be production.'
          })}
        </div>
      )}

      {allowanceShared && (
        <div className="mt-2 text-[12px] text-[#EA580C]">
          {t('admin.activityLog.notice.sharedAllowance', {
            defaultValue:
              'The provider allowance is shared with other environments. Remaining allowance shown here is an upper bound — other environments may already have spent some of it.'
          })}
        </div>
      )}
    </div>
  );
};

export default EnvironmentNotice;
